
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Loader2, BookOpen, TrendingDown, CheckCircle, Brain, FileText, Sparkles } from "lucide-react";
import { useNavigate } from 'react-router-dom';
import { supabase } from "@/lib/supabase";
import { useAuthSession } from "@/hooks/useAuthSession";
import { format } from "date-fns";
import { toast } from "sonner";
import { useCurrentAffairsPerformanceAnalysis } from "@/hooks/useCurrentAffairsPerformanceAnalysis";
import { ContentVisualizationGallery } from '../visualization/ContentVisualizationGallery';
import { AdaptiveKnowledgeGraph } from '../visualization/AdaptiveKnowledgeGraph';
import { MultiModalContentView } from '../learning/MultiModalContentView';

interface AdaptiveNotesProps {
  quizSessionId?: string;
  difficulty?: 'easy' | 'medium' | 'hard';
  topic?: string;
  date?: string;
}

export const CurrentAffairsAdaptiveNotes: React.FC<AdaptiveNotesProps> = ({
  quizSessionId,
  difficulty = 'medium',
  topic = 'Current Affairs',
  date
}) => {
  const [loading, setLoading] = useState(false);
  const [generatingNotes, setGeneratingNotes] = useState(false);
  const [adaptiveNotes, setAdaptiveNotes] = useState<{
    content: string;
    weakTopics: string[];
    focusAreas: string[];
    generatedAt: string;
    cognitiveLevel?: string;
  } | null>(null);
  const [weakAreas, setWeakAreas] = useState<{topic: string, score: number}[]>([]);
  const [activeTab, setActiveTab] = useState<'notes' | 'knowledge-graph' | 'multi-modal'>('notes');
  const [generatingVisualization, setGeneratingVisualization] = useState(false);
  const [visualizations, setVisualizations] = useState<{concept: string, imageUrl: string}[]>([]);
  const { userId } = useAuthSession();
  const navigate = useNavigate();
  const { analyzePerformance } = useCurrentAffairsPerformanceAnalysis();
  const [cognitiveProfile, setCognitiveProfile] = useState<{
    learningStyle?: string;
    preferredContentLength?: string;
    attentionSpan?: string;
  } | null>(null);
  
  // Fetch weak areas and existing adaptive notes
  useEffect(() => {
    if (!userId) return;
    
    const fetchData = async () => {
      setLoading(true);
      try {
        // First check if we have adaptive notes already generated for this session
        if (quizSessionId) {
          const { data: adaptiveData } = await supabase
            .from('adaptive_notes')
            .select('*')
            .eq('quiz_session_id', quizSessionId)
            .maybeSingle();
            
          if (adaptiveData) {
            setAdaptiveNotes({
              content: adaptiveData.content,
              weakTopics: adaptiveData.question_analytics?.weak_topics || [],
              focusAreas: adaptiveData.question_analytics?.focus_areas || [],
              generatedAt: adaptiveData.created_at,
              cognitiveLevel: adaptiveData.question_analytics?.cognitive_level
            });
            return;
          }
        }
        
        // Get user's cognitive profile for adaptive formatting
        const { data: profileData } = await supabase
          .from('cognitive_profiles')
          .select('learning_style, preferred_content_length, attention_span')
          .eq('user_id', userId)
          .maybeSingle();
          
        if (profileData) {
          // Fix #1: Map database field names to our state property names
          setCognitiveProfile({
            learningStyle: profileData.learning_style,
            preferredContentLength: profileData.preferred_content_length,
            attentionSpan: profileData.attention_span
          });
        }
        
        // Use the performance analysis hook to get a comprehensive view
        const performanceData = await analyzePerformance();
        
        if (performanceData) {
          // Fix #2: Map the weakTopics to the expected format with 'score' property
          setWeakAreas(performanceData.weakTopics.map(topic => ({
            topic: topic.topic,
            score: topic.accuracy
          })));
        } else {
          // Fall back to the previous implementation if performance analysis not available
          const { data: performanceData } = await supabase
            .from('current_affairs_performance')
            .select('*')
            .eq('user_id', userId)
            .order('created_at', { ascending: false })
            .limit(10);
          
          if (performanceData && performanceData.length > 0) {
            // Analyze performance to identify weak areas
            const topicPerformance: Record<string, {correct: number, total: number}> = {};
            
            performanceData.forEach(record => {
              const topic = record.topic;
              if (!topicPerformance[topic]) {
                topicPerformance[topic] = { correct: 0, total: 0 };
              }
              topicPerformance[topic].correct += record.correct_count || 0;
              topicPerformance[topic].total += record.question_count || 0;
            });
            
            // Calculate scores and find weak areas
            const weakTopics = Object.entries(topicPerformance)
              .map(([topic, { correct, total }]) => ({
                topic,
                score: total > 0 ? (correct / total) * 100 : 0
              }))
              .filter(item => item.score < 70) // Topics with less than 70% score are considered weak
              .sort((a, b) => a.score - b.score);
            
            setWeakAreas(weakTopics);
          }
        }
      } catch (error) {
        console.error('Error fetching adaptive data:', error);
        toast.error('Failed to load adaptive notes data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [userId, quizSessionId, analyzePerformance]);
  
  // Generate adaptive notes based on performance
  const generateAdaptiveNotes = async () => {
    if (!userId) {
      toast.error('Please log in to generate adaptive notes');
      return;
    }
    
    setGeneratingNotes(true);
    try {
      // Get detailed performance analysis for better adaptive content
      const performanceData = await analyzePerformance();
      
      // Request parameters for adaptive notes generation with enhanced data
      const params = {
        userId,
        sessionId: quizSessionId,
        difficulty,
        topic,
        date,
        weakAreas: weakAreas.map(w => w.topic),
        performanceData: {
          overallAccuracy: performanceData?.overallAccuracy || 0,
          averageTimePerQuestion: performanceData?.averageTimePerQuestion || 0,
          recentImprovement: performanceData?.recentImprovement || 0,
          weakTopics: performanceData?.weakTopics || [],
        },
        cognitivePreferences: {
          learningStyle: cognitiveProfile?.learningStyle || 'visual',
          contentLength: cognitiveProfile?.preferredContentLength || 'medium',
          attentionSpan: cognitiveProfile?.attentionSpan || 'medium'
        }
      };
      
      // Call the edge function to generate adaptive notes with enhanced parameters
      const { data, error } = await supabase.functions.invoke('generate-current-affairs-notes', {
        body: params
      });
      
      if (error) throw error;
      
      if (data?.content) {
        // Store the generated notes in the database with enhanced metadata
        const { data: savedNote, error: saveError } = await supabase
          .from('adaptive_notes')
          .insert({
            user_id: userId,
            quiz_session_id: quizSessionId,
            subject: 'Current Affairs',
            category: topic,
            content: data.content,
            question_analytics: {
              weak_topics: data.weakTopics || weakAreas.map(w => w.topic),
              focus_areas: data.focusAreas || [],
              generated_from_performance: true,
              cognitive_level: data.cognitiveLevel || 'standard',
              performance_summary: {
                accuracy: performanceData?.overallAccuracy || 0,
                improvement: performanceData?.recentImprovement || 0
              }
            }
          })
          .select('*')
          .single();
        
        if (saveError) throw saveError;
        
        setAdaptiveNotes({
          content: data.content,
          weakTopics: data.weakTopics || weakAreas.map(w => w.topic),
          focusAreas: data.focusAreas || [],
          generatedAt: new Date().toISOString(),
          cognitiveLevel: data.cognitiveLevel
        });
        
        toast.success('Adaptive notes generated successfully');
      } else {
        throw new Error('No content returned from notes generation');
      }
    } catch (error) {
      console.error('Error generating adaptive notes:', error);
      toast.error('Failed to generate adaptive notes');
    } finally {
      setGeneratingNotes(false);
    }
  };
  
  // New function to generate visualizations for focus areas
  const generateVisualizationsForFocusAreas = async () => {
    if (!adaptiveNotes?.weakTopics.length) return;
    
    setGeneratingVisualization(true);
    
    try {
      const newVisualizations = [];
      
      // Generate visualizations for up to 3 focus areas
      for (const topic of adaptiveNotes.weakTopics.slice(0, 3)) {
        try {
          const { data, error } = await supabase.functions.invoke('generate-note-visuals', {
            body: { 
              concept: topic,
              style: 'educational'
            }
          });
          
          if (error) throw error;
          
          if (data?.image) {
            newVisualizations.push({
              concept: topic,
              imageUrl: data.image
            });
          }
        } catch (err) {
          console.error(`Error generating visualization for ${topic}:`, err);
        }
      }
      
      setVisualizations(newVisualizations);
      if (newVisualizations.length > 0) {
        toast.success(`Generated ${newVisualizations.length} visualizations`);
      }
    } catch (error) {
      console.error('Error generating visualizations:', error);
      toast.error('Failed to generate visualizations');
    } finally {
      setGeneratingVisualization(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-10">
        <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
        <p className="text-muted-foreground">Loading adaptive learning data...</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      <Card className="bg-gradient-to-br from-blue-50 to-indigo-50">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center">
            <Brain className="mr-2 h-5 w-5 text-primary" />
            Adaptive Current Affairs Notes
          </CardTitle>
          <CardDescription>
            Notes tailored to your performance in current affairs quizzes
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          {adaptiveNotes ? (
            <div className="space-y-4">
              <Tabs defaultValue="notes" value={activeTab} onValueChange={(value) => setActiveTab(value as any)}>
                <TabsList className="grid grid-cols-3 mb-4">
                  <TabsTrigger value="notes" className="flex items-center gap-2">
                    <FileText className="h-4 w-4" /> Notes
                  </TabsTrigger>
                  <TabsTrigger value="knowledge-graph" className="flex items-center gap-2">
                    <Sparkles className="h-4 w-4" /> Knowledge Graph
                  </TabsTrigger>
                  <TabsTrigger value="multi-modal" className="flex items-center gap-2">
                    <BookOpen className="h-4 w-4" /> Learning Modes
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="notes" className="space-y-4">
                  <div className="bg-white rounded-md p-4 border">
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="text-sm font-semibold">Generated on {format(new Date(adaptiveNotes.generatedAt), "PPP")}</h3>
                      <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full">
                        {difficulty.toUpperCase()}
                      </span>
                    </div>
                    
                    {adaptiveNotes.weakTopics.length > 0 && (
                      <div className="mb-4">
                        <h4 className="text-sm font-medium text-muted-foreground mb-1">Focus areas:</h4>
                        <div className="flex flex-wrap gap-1">
                          {adaptiveNotes.weakTopics.map((topic, i) => (
                            <span key={i} className="bg-red-50 text-red-700 text-xs px-2 py-0.5 rounded-full">
                              {topic}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    <div 
                      className="prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ __html: adaptiveNotes.content }}
                    />
                  </div>
                  
                  {/* Visualization Section */}
                  <div className="bg-white rounded-md p-4 border">
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="text-sm font-semibold">Visual Learning Resources</h3>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={generateVisualizationsForFocusAreas}
                        disabled={generatingVisualization}
                      >
                        {generatingVisualization ? (
                          <>
                            <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <Sparkles className="mr-2 h-3 w-3" />
                            Generate Visuals
                          </>
                        )}
                      </Button>
                    </div>

                    {visualizations.length > 0 ? (
                      <ContentVisualizationGallery visualizations={visualizations} />
                    ) : (
                      <div className="text-center py-4 border border-dashed rounded-md bg-gray-50">
                        <p className="text-sm text-muted-foreground">
                          Click "Generate Visuals" to create visualizations for your focus areas
                        </p>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex justify-end">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => navigate('/current-affairs')}
                    >
                      Back to Current Affairs
                    </Button>
                  </div>
                </TabsContent>
                
                <TabsContent value="knowledge-graph">
                  <AdaptiveKnowledgeGraph 
                    topics={adaptiveNotes.weakTopics} 
                    content={adaptiveNotes.content} 
                  />
                </TabsContent>
                
                <TabsContent value="multi-modal">
                  <MultiModalContentView 
                    content={adaptiveNotes.content} 
                    topics={adaptiveNotes.weakTopics}
                    preferredStyle={cognitiveProfile?.learningStyle || 'visual'}
                  />
                </TabsContent>
              </Tabs>
            </div>
          ) : (
            <div className="space-y-4">
              {weakAreas.length > 0 ? (
                <>
                  <Alert variant="destructive" className="bg-red-50 text-red-800 border-red-200">
                    <TrendingDown className="h-4 w-4" />
                    <AlertTitle>Improvement Areas Detected</AlertTitle>
                    <AlertDescription>
                      Based on your quiz performance, you could improve in these areas:
                    </AlertDescription>
                  </Alert>
                  
                  <div className="grid grid-cols-1 gap-2 mt-2">
                    {weakAreas.map((area, index) => (
                      <div key={index} className="flex justify-between items-center p-2 bg-white rounded border">
                        <span>{area.topic}</span>
                        <span className="text-sm font-medium text-red-600">{Math.round(area.score)}% accuracy</span>
                      </div>
                    ))}
                  </div>
                  
                  <Button 
                    onClick={generateAdaptiveNotes} 
                    disabled={generatingNotes}
                    className="w-full"
                  >
                    {generatingNotes ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Generating adaptive notes...
                      </>
                    ) : (
                      <>
                        <FileText className="mr-2 h-4 w-4" />
                        Generate Adaptive Study Notes
                      </>
                    )}
                  </Button>
                </>
              ) : (
                <div className="text-center py-6">
                  <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-2" />
                  <h3 className="text-lg font-medium mb-1">Great Work!</h3>
                  <p className="text-muted-foreground mb-4">
                    We don't see any specific areas that need improvement based on your quiz history.
                  </p>
                  <Button 
                    variant="outline" 
                    onClick={() => navigate('/current-affairs/quiz')}
                    size="sm"
                  >
                    <BookOpen className="mr-2 h-4 w-4" />
                    Take a Current Affairs Quiz
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Make sure we have a default export
export default CurrentAffairsAdaptiveNotes;
